import FolderIcon from 'assets/icons/cabinet/folder-icon-sm.svg';
import DOCIcon from 'assets/icons/cabinet/DOC.svg';
import CSVIcon from 'assets/icons/cabinet/CSV.svg';
import PDFIcon from 'assets/icons/cabinet/PDF.svg';
import JPGIcon from 'assets/icons/cabinet/JPG.svg';
import PNGIcon from 'assets/icons/cabinet/PNG.svg';
import GIFIcon from 'assets/icons/cabinet/GIF.svg';
import WEBPIcon from 'assets/icons/cabinet/WEBP.svg';
import TiffIcon from 'assets/icons/cabinet/TIFF.svg';
import AvifIcon from 'assets/icons/cabinet/AVIF.svg';
import HEICIcon from 'assets/icons/cabinet/HEIC.svg';
import HEIFIcon from 'assets/icons/cabinet/HEIF.svg';
import XlsIcon from 'assets/icons/cabinet/XLS.svg';

import {
  FileCabinetLocation,
  FileCabinetTypeEnum,
  FileCabinetUserPermission,
  IFolder,
} from 'redux-store/api/file-cabinet';
import { ICompanyRole, PERMISSION_UI_TYPES_ENUM } from 'redux-store/api/company-roles';
import { IProjectProfiles } from 'redux-store/api/projects';
import { FieldArrayWithId, UseFieldArrayUpdate } from 'react-hook-form';
import { IRolePermissionsForm } from 'utils/types';

export const getMimeTypeFromFileName = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return 'application/pdf';
    case 'doc':
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xls':
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'ppt':
    case 'pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    case 'tiff':
    case 'tif':
      return 'image/tiff';
    case 'avif':
      return 'image/avif';
    case 'heic':
      return 'image/heic';
    case 'heif':
      return 'image/heif';
    case 'csv':
      return 'text/csv';
    case 'mp4':
      return 'video/mp4';
    case 'mov':
      return 'video/quicktime';
    case 'avi':
      return 'video/x-msvideo';
    case 'mkv':
      return 'video/x-matroska';
    case 'webm':
      return 'video/webm';
    case 'mp3':
      return 'audio/mpeg';
    case 'wav':
      return 'audio/wav';
    case 'm4a':
      return 'audio/mp4';
    case 'ogg':
      return 'audio/ogg';
    default:
      return 'application/octet-stream'; // Default for unknown file types
  }
};

export const getRowIcon = (type: string, mimeType?: string) => {
  switch (type) {
    case 'file':
      switch (mimeType) {
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        case 'application/vnd.ms-powerpoint':
        case 'application/msword':
          return DOCIcon;

        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        case 'application/vnd.ms-excel':
          return XlsIcon;

        case 'image/jpeg':
        case 'image/jpg':
          return JPGIcon;

        case 'image/png':
          return PNGIcon;

        case 'image/gif':
          return GIFIcon;

        case 'image/webp':
          return WEBPIcon;

        case 'image/tiff':
          return TiffIcon;

        case 'image/avif':
          return AvifIcon;

        case 'image/heic':
          return HEICIcon;

        case 'image/heif':
          return HEIFIcon;

        case 'text/csv':
          return CSVIcon;

        case 'application/pdf':
          return PDFIcon;

        default:
          return DOCIcon;
      }

    case 'folder':
      return FolderIcon;

    default:
      return DOCIcon;
  }
};

export const PERMISSION_SUBJECT_CONSTANTS = {
  Surveys: 'SURVEYS',
  Selections: 'SELECTIONS_FILE_CABINET',
  Plans: 'PLANS',
  Permits: 'PERMITS',
  Invoices: 'INVOICES_FILE_CABINET',
  Inspections: 'INSPECTIONS',
  Estimates: 'ESTIMATES',
  Contracts: 'CONTRACTS',
};

export const destinationFoldersOptions = (data: IFolder[]) => {
  if (!data?.length) {
    return [];
  }

  const foldersList = data?.map((folder) => ({
    label: folder.name,
    value: folder.id,
  }));

  return foldersList;
};

export const defaultRolesHelper = (roles: ICompanyRole[] = []) => {
  return roles.map(({ id, name }) => {
    const isAdmin = name === 'ADMIN';

    return {
      id,
      name,
      permissions: {
        canRead: isAdmin,
        canAddUpdate: isAdmin,
        canDelete: isAdmin,
      },
    };
  });
};

export const defaultCustomProjectFolderRolesHelper = (roles: ICompanyRole[] = []) => {
  return roles.map(({ id, name }) => {
    const hasFullAccess = name === 'ADMIN' || name === 'EMPLOYEE';

    return {
      id,
      name,
      permissions: {
        canRead: hasFullAccess,
        canAddUpdate: hasFullAccess,
        canDelete: hasFullAccess,
      },
    };
  });
};

export const isCustomProjectFolder = (folder?: IFolder): boolean =>
  folder?.type === FileCabinetTypeEnum.Folder && Array.isArray(folder?.roles);

export const getFolderPermissionsForUser = (
  folder?: IFolder,
  userMe?: { id: number; recentCompanyId: number | null; roles: { companyId: number; roleId: number }[] | null },
  companyId?: string | number | null,
) => {
  if (!folder || !userMe) {
    return undefined;
  }

  const profilePermissions = folder.users?.find(
    ({ id }) => String(id) === String(userMe.id),
  )?.permissions;

  if (profilePermissions) {
    return profilePermissions;
  }

  if (!folder.roles?.length) {
    return undefined;
  }

  const resolvedCompanyId =
    companyId ?? localStorage.getItem('company-id') ?? userMe.recentCompanyId;
  const userRole = userMe.roles?.find(
    ({ companyId: roleCompanyId }) =>
      String(roleCompanyId) === String(resolvedCompanyId),
  );

  if (!userRole) {
    return undefined;
  }

  return folder.roles.find(
    ({ id }) => String(id) === String(userRole.roleId),
  )?.permissions;
};

export const handleFileSizeCheck = (size: string | number) => {
  if (size) {
    const inMegabytes = Number(size) / (1024 * 1024);
    const inGigabytes = Number(size) / (1024 * 1024 * 1024);

    if (inGigabytes > 1) {
      return `${inGigabytes} GB`;
    }

    if (inMegabytes > 1) {
      return `${inMegabytes} MB`;
    }

    if (inMegabytes < 1 && inMegabytes > 0) {
      return `${inMegabytes?.toFixed(4)} MB`;
    }

    return `${size} B`;
  }

  return `0 B`;
};

export const projectCabinetUsersList = (profiles, userId: number | undefined) => {
  if (!profiles || !profiles?.length) {
    return [];
  }

  const usersList = profiles?.map((user: IProjectProfiles) => {
    const isMe = userId === user.userId && !user.isOwner;
    return {
      id: user?.id,
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      avatar: user?.avatar || null,
      role: user?.role,
      jobTitle: user?.jobTitle,
      isOwner: user?.isOwner,
      permissions: {
        canRead: isMe,
        canAddUpdate: isMe,
        canDelete: isMe,
      },
    };
  });

  return usersList;
};

export const postProjectCabinetUsersList = (profiles) => {
  if (!profiles || !profiles?.length) {
    return [];
  }

  const usersList = profiles?.map((user) => {
    return {
      profileId: user?.id,
      permissions: {
        canRead: user?.permissions?.canRead,
        canAddUpdate: user?.permissions?.canAddUpdate,
        canDelete: user?.permissions?.canDelete,
      },
    };
  });

  return usersList;
};

export const checkUserAddAccess = (
  publicFolders: boolean,
  canCreate: boolean,
  myPermissions: FileCabinetUserPermission,
  isRootLevel: boolean,
  isOwner?: boolean
) => {
  if (publicFolders) {
    return false;
  }

  if (isOwner) {
    return true;
  }

  if (isRootLevel && canCreate) {
    return true;
  }

  if (!isRootLevel) {
    return myPermissions?.canAddUpdate;
  }

  return false;
};

export const fileCabinetPermissionsField = (
  fields: FieldArrayWithId<IRolePermissionsForm, 'permissions', 'id'>[]
) => {
  const cabinetPermissionsField = fields?.find(({ title }) => title === 'File cabinet');

  if (!cabinetPermissionsField) {
    return [];
  }

  const sortedPermissions = cabinetPermissionsField?.items?.sort((a, b) => {
    if (
      a.type === PERMISSION_UI_TYPES_ENUM?.TOGGLE &&
      b.type === PERMISSION_UI_TYPES_ENUM?.CHECKBOX
    ) {
      return -1;
    } else if (
      a.type === PERMISSION_UI_TYPES_ENUM?.CHECKBOX &&
      b.type === PERMISSION_UI_TYPES_ENUM?.TOGGLE
    ) {
      return 1;
    } else if (
      a.type === PERMISSION_UI_TYPES_ENUM?.TOGGLE &&
      b.type === PERMISSION_UI_TYPES_ENUM?.TOGGLE
    ) {
      const titleOrder = {
        CREATE_FOLDERS: 1,
        MANAGE_FILE_CABINET: 2,
        FILE_CABINET_DEFAULT_FOLDERS: 3,
        VIEW_PROJECT_FOLDERS: 4,
      };

      return titleOrder[a.permissionSubject] - titleOrder[b.permissionSubject];
    } else {
      return 0;
    }
  });

  return sortedPermissions;
};

/**
 * App routes where the header File Cabinet modal drives folder navigation via the URL hash
 * (`/current/path#folderId`) instead of navigating to `/file-cabinet/...`.
 *
 * Includes top-level project areas that are not nested under `/project-dashboard/...`
 * but still embed the modal (e.g. `/media/:projectId`, `/feedbacks/:projectId`).
 */
export const usesProjectFileCabinetHashNavigation = (pathname: string): boolean =>
  pathname.includes('selections') ||
  pathname.includes('account-dashboard') ||
  pathname.includes('project-dashboard') ||
  pathname.startsWith('/media/') ||
  pathname.startsWith('/feedbacks/') ||
  pathname.startsWith('/project-settings/');
